import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';

const getOrigin = (position) => {
  switch (position) {
    case 'bottom-left':  return 'top left';
    case 'bottom-right': return 'top right';
    case 'top-left':     return 'bottom left';
    case 'top-right':    return 'bottom right';
    default:             return 'top right';
  }
};

const getPositionClasses = (position) => {
  switch (position) {
    case 'bottom-left':  return 'left-0 top-full mt-1';
    case 'bottom-right': return 'right-0 top-full mt-1';
    case 'top-left':     return 'left-0 bottom-full mb-1';
    case 'top-right':    return 'right-0 bottom-full mb-1';
    default:             return 'right-0 top-full mt-1';
  }
};

const Dropdown = ({ trigger, items, position = 'bottom-right' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemClick = (onClick) => {
    setIsOpen(false);
    if (onClick) onClick();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div onClick={() => setIsOpen(prev => !prev)} className="cursor-pointer">
        {trigger}
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -4 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className={`absolute z-50 py-1 min-w-[180px] rounded-xl ${getPositionClasses(position)}`}
            style={{
              backgroundColor: '#18181b',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 12px 32px rgba(0,0,0,0.6)',
              transformOrigin: getOrigin(position),
            }}
          >
            {items.map((item, index) => (
              <div
                key={index}
                onClick={() => handleItemClick(item.onClick)}
                className="px-3 py-2 flex items-center gap-2.5 text-zinc-300 hover:bg-white/[0.05] hover:text-white cursor-pointer text-sm transition-colors duration-150"
              >
                {item.icon && <span className="shrink-0">{item.icon}</span>}
                <span>{item.label}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

Dropdown.propTypes = {
  trigger: PropTypes.node.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      icon: PropTypes.node,
    })
  ).isRequired,
  position: PropTypes.oneOf(['bottom-left', 'bottom-right', 'top-left', 'top-right']),
};

export default Dropdown;
